import axios from 'axios'
import type {AxiosError} from 'axios'
import {
  PmsTemplateDto,
  PmsTemplateTaskDto,
  PmsPlanDto,
  PmsPlanLineDto,
  RunningHourCounterDto,
  RunningHourReadingDto,
  RunningHourReadingImpactDto,
  PagedResult,
  PmsJobDto,
  PmsJobCombinedDto,
  PmsJobEventDto,
  PmsJobSpareUsageDto,
  PmsJobAttachmentDto,
  PmsJobAttachmentFileType,
  PmsJobCompletionDto,
  PmsJobRecord,
  PmsPlanLineRecord,
  PmsJobState,
  ComplianceReportDto,
  JobHistoryReportDto,
  OverhaulHistoryReportDto,
  PmsPostponementRequestDto,
  SparesConsumptionReportDto,
  DefectsIntegrationReportDto,
  DefectJobReportDto,
  BulkJobOperationResult,
  PmsCommandCenterDto,
  PmsActionCenterDto,
} from './_models'
import type {QHSEDefectDto} from '../../../QHSE/core/_models'

const API_URL = process.env.REACT_APP_API_URL
const PMS_API_URL = `${API_URL}/pms`

// ===============================
// HELPERS
// ===============================

const pickAxiosMessage = (err: unknown, fallback: string) => {
  const ae = err as AxiosError<any>
  return ae?.response?.data?.message ?? ae?.response?.data?.error ?? ae?.message ?? fallback
}

// ===============================
// TEMPLATES
// ===============================

export const getPmsTemplates = async (q?: string): Promise<PmsTemplateDto[]> => {
  try {
    const params = q ? {q} : {}
    const {data} = await axios.get(`${PMS_API_URL}/templates`, {params})
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Error fetching PMS templates:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching PMS templates'))
  }
}

export const getPmsTemplate = async (id: number): Promise<PmsTemplateDto> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/templates/${id}`)
    return data
  } catch (error) {
    console.error('Error fetching PMS template:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching PMS template'))
  }
}

export const createPmsTemplate = async (dto: PmsTemplateDto): Promise<PmsTemplateDto> => {
  try {
    const {data} = await axios.post(`${PMS_API_URL}/templates`, dto)
    return data
  } catch (error) {
    console.error('Error creating PMS template:', error)
    throw new Error(pickAxiosMessage(error, 'Error creating PMS template'))
  }
}

export const updatePmsTemplate = async (
  id: number,
  dto: PmsTemplateDto
): Promise<PmsTemplateDto> => {
  try {
    const {data} = await axios.put(`${PMS_API_URL}/templates/${id}`, dto)
    return data
  } catch (error) {
    console.error('Error updating PMS template:', error)
    throw new Error(pickAxiosMessage(error, 'Error updating PMS template'))
  }
}

export const deletePmsTemplate = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${PMS_API_URL}/templates/${id}`)
  } catch (error) {
    console.error('Error deleting PMS template:', error)
    throw new Error(pickAxiosMessage(error, 'Error deleting PMS template'))
  }
}

// ===============================
// TEMPLATE TASKS
// ===============================

export const getPmsTemplateTasks = async (templateId: number): Promise<PmsTemplateTaskDto[]> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/templates/${templateId}/tasks`)
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Error fetching template tasks:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching template tasks'))
  }
}

export const getPmsTemplateTask = async (taskId: number): Promise<PmsTemplateTaskDto> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/templates/tasks/${taskId}`)
    return data
  } catch (error) {
    console.error('Error fetching template task:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching template task'))
  }
}

export const createPmsTemplateTask = async (
  templateId: number,
  dto: PmsTemplateTaskDto
): Promise<PmsTemplateTaskDto> => {
  try {
    const {data} = await axios.post(`${PMS_API_URL}/templates/${templateId}/tasks`, dto)
    return data
  } catch (error) {
    console.error('Error creating template task:', error)
    throw new Error(pickAxiosMessage(error, 'Error creating template task'))
  }
}

export const updatePmsTemplateTask = async (
  taskId: number,
  dto: PmsTemplateTaskDto
): Promise<PmsTemplateTaskDto> => {
  try {
    const {data} = await axios.put(`${PMS_API_URL}/templates/tasks/${taskId}`, dto)
    return data
  } catch (error) {
    console.error('Error updating template task:', error)
    throw new Error(pickAxiosMessage(error, 'Error updating template task'))
  }
}

export const deletePmsTemplateTask = async (taskId: number): Promise<void> => {
  try {
    await axios.delete(`${PMS_API_URL}/templates/tasks/${taskId}`)
  } catch (error) {
    console.error('Error deleting template task:', error)
    throw new Error(pickAxiosMessage(error, 'Error deleting template task'))
  }
}

// ===============================
// PLANS
// ===============================

export const getPmsPlans = async (vesselId?: number): Promise<PmsPlanDto[]> => {
  try {
    const params = vesselId ? {vesselId} : {}
    const {data} = await axios.get(`${PMS_API_URL}/plans`, {params})
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Error fetching PMS plans:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching PMS plans'))
  }
}

export const getPmsPlan = async (id: number): Promise<PmsPlanDto> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/plans/${id}`)
    return data
  } catch (error) {
    console.error('Error fetching PMS plan:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching PMS plan'))
  }
}

export const createPmsPlan = async (dto: PmsPlanDto): Promise<PmsPlanDto> => {
  try {
    const {data} = await axios.post(`${PMS_API_URL}/plans`, dto)
    return data
  } catch (error) {
    console.error('Error creating PMS plan:', error)
    throw new Error(pickAxiosMessage(error, 'Error creating PMS plan'))
  }
}

export const updatePmsPlan = async (id: number, dto: PmsPlanDto): Promise<PmsPlanDto> => {
  try {
    const {data} = await axios.put(`${PMS_API_URL}/plans/${id}`, dto)
    return data
  } catch (error) {
    console.error('Error updating PMS plan:', error)
    throw new Error(pickAxiosMessage(error, 'Error updating PMS plan'))
  }
}

export const deletePmsPlan = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${PMS_API_URL}/plans/${id}`)
  } catch (error) {
    console.error('Error deleting PMS plan:', error)
    throw new Error(pickAxiosMessage(error, 'Error deleting PMS plan'))
  }
}

export const derivePlanFromTemplate = async (
  planId: number,
  templateId: number,
  replace: boolean = false
): Promise<{planId: number; templateId: number; linesCreated: number; replace: boolean}> => {
  try {
    const {data} = await axios.post(`${PMS_API_URL}/plans/${planId}/derive-from-template`, null, {
      params: {templateId, replace},
    })
    return data
  } catch (error) {
    console.error('Error deriving plan from template:', error)
    throw new Error(pickAxiosMessage(error, 'Error deriving plan from template'))
  }
}

// ===============================
// PLAN LINES
// ===============================

export const getPmsPlanLines = async (planId: number): Promise<PmsPlanLineDto[]> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/plans/${planId}/lines`)
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Error fetching plan lines:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching plan lines'))
  }
}

export const createPmsPlanLine = async (
  planId: number,
  dto: PmsPlanLineDto
): Promise<PmsPlanLineDto> => {
  try {
    const {data} = await axios.post(`${PMS_API_URL}/plans/${planId}/lines`, dto)
    return data
  } catch (error) {
    console.error('Error creating plan line:', error)
    throw new Error(pickAxiosMessage(error, 'Error creating plan line'))
  }
}

export const updatePmsPlanLine = async (
  id: number,
  dto: PmsPlanLineDto
): Promise<PmsPlanLineDto> => {
  try {
    const {data} = await axios.put(`${PMS_API_URL}/plans/lines/${id}`, dto)
    return data
  } catch (error) {
    console.error('Error updating plan line:', error)
    throw new Error(pickAxiosMessage(error, 'Error updating plan line'))
  }
}

// ===============================
// DASHBOARD
// ===============================

export const getPmsCommandCenterStats = async (
  vesselIds?: number[]
): Promise<PmsCommandCenterDto> => {
  try {
    const params: any = {}
    if (vesselIds && vesselIds.length > 0) params.vesselIds = vesselIds.join(',')
    const {data} = await axios.get(`${PMS_API_URL}/dashboard/command-center`, {params})
    return data
  } catch (error) {
    console.error('Error fetching PMS command center stats:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching PMS command center stats'))
  }
}

export const getPmsActionCenterStats = async (
  vesselIds?: number[]
): Promise<PmsActionCenterDto> => {
  try {
    const params: any = {}
    if (vesselIds && vesselIds.length > 0) params.vesselIds = vesselIds.join(',')
    const {data} = await axios.get(`${PMS_API_URL}/dashboard/action-center`, {params})
    return data
  } catch (error) {
    console.error('Error fetching PMS action center stats:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching PMS action center stats'))
  }
}

export const getPmsPlanLine = async (lineId: number): Promise<PmsPlanLineDto> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/plans/lines/${lineId}`)
    return data
  } catch (error) {
    console.error('Error fetching plan line:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching plan line'))
  }
}

export const deletePmsPlanLine = async (lineId: number): Promise<void> => {
  try {
    await axios.delete(`${PMS_API_URL}/plans/lines/${lineId}`)
  } catch (error) {
    console.error('Error deleting plan line:', error)
    throw new Error(pickAxiosMessage(error, 'Error deleting plan line'))
  }
}

// ===============================
// JOBS
// ===============================

export const searchPmsJobs = async (
  vesselId?: number,
  state?: PmsJobState,
  fromDate?: string,
  toDate?: string,
  defectId?: number,
  equipmentId?: number,
  componentId?: number,
  subComponentId?: number,
  criticality?: string,
  page?: number,
  size?: number,
  sortBy?: string,
  sortDir?: 'asc' | 'desc'
): Promise<PmsJobDto[]> => {
  try {
    const params: any = {}
    if (vesselId) params.vesselId = vesselId
    if (state) params.state = state
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    if (defectId) params.defectId = defectId
    if (equipmentId) params.equipmentId = equipmentId
    if (componentId) params.componentId = componentId
    if (subComponentId) params.subComponentId = subComponentId
    if (criticality) params.criticality = criticality
    if (page !== undefined) params.page = page
    if (size !== undefined) params.size = size
    if (sortBy) params.sortBy = sortBy
    if (sortDir) params.sortDir = sortDir
    const {data} = await axios.get(`${PMS_API_URL}/jobs`, {params})
    if (Array.isArray(data)) return data
    if (data && Array.isArray(data.content)) return data.content
    return []
  } catch (error) {
    console.error('Error searching PMS jobs:', error)
    throw new Error(pickAxiosMessage(error, 'Error searching PMS jobs'))
  }
}

export const searchPmsJobsPage = async (params: {
  vesselId?: number
  state?: PmsJobState
  fromDate?: string
  toDate?: string
  defectId?: number
  equipmentId?: number
  componentId?: number
  subComponentId?: number
  criticality?: string
  page?: number
  size?: number
  sortBy?: string
  sortDir?: 'asc' | 'desc'
}): Promise<PagedResult<PmsJobDto>> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/jobs`, {params})
    if (data && Array.isArray(data.content)) return data
    return {
      content: Array.isArray(data) ? data : [],
      totalElements: data?.totalElements ?? 0,
      totalPages: data?.totalPages ?? 1,
      size: data?.size ?? 20,
      number: data?.number ?? 0,
    }
  } catch (error) {
    console.error('Error searching PMS jobs page:', error)
    throw new Error(pickAxiosMessage(error, 'Error searching PMS jobs page'))
  }
}

export const searchPmsJobsCombined = async (params: {
  vesselId?: number
  state?: string
  fromDate?: string
  toDate?: string
  criticality?: string
  scheduleType?: string
  equipmentIds?: number[]
  q?: string
  page?: number
  size?: number
  sortBy?: string
  sortDir?: 'asc' | 'desc'
  dateFilterMode?: string
  jobNature?: string
}): Promise<PagedResult<PmsJobCombinedDto>> => {
  try {
    const query: any = {}
    if (params.vesselId) query.vesselId = params.vesselId
    if (params.state) query.state = params.state
    if (params.fromDate) query.fromDate = params.fromDate
    if (params.toDate) query.toDate = params.toDate
    if (params.criticality) query.criticality = params.criticality
    if (params.scheduleType) query.scheduleType = params.scheduleType
    if (params.equipmentIds && params.equipmentIds.length > 0) {
      query.equipmentIds = params.equipmentIds.join(',')
    }
    if (params.q) query.q = params.q
    if (params.page != null) query.page = params.page
    if (params.size != null) query.size = params.size
    if (params.sortBy) query.sortBy = params.sortBy
    if (params.sortDir) query.sortDir = params.sortDir
    if (params.dateFilterMode) query.dateFilterMode = params.dateFilterMode
    if (params.jobNature) query.jobNature = params.jobNature
    const {data} = await axios.get(`${PMS_API_URL}/jobs/combined`, {params: query})
    return data
  } catch (error) {
    console.error('Error searching PMS jobs (combined):', error)
    throw new Error(pickAxiosMessage(error, 'Error searching PMS jobs'))
  }
}

export const getPmsJobSummaryStats = async (
  vesselId?: number
): Promise<{
  total: number
  activeTotal?: number
  overdue: number
  overdueUnplanned?: number
  scheduledCompleted?: number
  scheduled: number
  completed: number
}> => {
  try {
    const params: any = {}
    if (vesselId) params.vesselId = vesselId
    const {data} = await axios.get(`${PMS_API_URL}/jobs/summary-stats`, {params})
    return data
  } catch (error) {
    console.error('Error fetching PMS job summary stats:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching PMS job summary stats'))
  }
}

export const getPmsJob = async (id: number): Promise<PmsJobDto> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/jobs/${id}`)
    return data
  } catch (error) {
    console.error('Error fetching PMS job:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching PMS job'))
  }
}

// Display version used by the PMS UI when a masked job must be opened safely.
export const getPmsJobDisplay = async (id: number): Promise<PmsJobDto> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/jobs/${id}/display`)
    return data
  } catch (error) {
    console.error('Error fetching PMS job display record:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching PMS job details'))
  }
}

export const getMaskableOverdueJobs = async (vesselId: number): Promise<PmsJobDto[]> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/jobs/masking/overdue`, {
      params: {vesselId},
    })
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Error fetching maskable overdue jobs:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching maskable overdue jobs'))
  }
}

export const applyJobMasking = async (params: {
  jobIds: number[]
  masked: boolean
}): Promise<{updated: number; masked: boolean}> => {
  try {
    const {data} = await axios.post(`${PMS_API_URL}/jobs/masking/apply`, params)
    return data
  } catch (error) {
    console.error('Error applying PMS job masking:', error)
    throw new Error(pickAxiosMessage(error, 'Error applying PMS job masking'))
  }
}

export const unmaskAllJobs = async (
  vesselId: number
): Promise<{updated: number; masked: boolean}> => {
  try {
    const {data} = await axios.post(`${PMS_API_URL}/jobs/masking/unmask-all`, {vesselId})
    return data
  } catch (error) {
    console.error('Error unmasking PMS jobs:', error)
    throw new Error(pickAxiosMessage(error, 'Error unmasking PMS jobs'))
  }
}

export const createPmsJob = async (dto: Partial<PmsJobDto>): Promise<PmsJobDto> => {
  try {
    const {data} = await axios.post(`${PMS_API_URL}/jobs`, dto)
    return data
  } catch (error) {
    console.error('Error creating PMS job:', error)
    throw new Error(pickAxiosMessage(error, 'Error creating PMS job'))
  }
}

export const updatePmsJob = async (id: number, dto: PmsJobDto): Promise<PmsJobDto> => {
  try {
    const {data} = await axios.put(`${PMS_API_URL}/jobs/${id}`, dto)
    return data
  } catch (error) {
    console.error('Error updating PMS job:', error)
    throw new Error(pickAxiosMessage(error, 'Error updating PMS job'))
  }
}

export const reassignPmsJob = async (
  id: number,
  crewUndertaking: string,
  reason?: string
): Promise<PmsJobDto> => {
  try {
    const params: any = {crewUndertaking}
    if (reason) params.reason = reason
    const {data} = await axios.post(`${PMS_API_URL}/jobs/${id}/reassign`, null, {params})
    return data
  } catch (error) {
    console.error('Error reassigning PMS job:', error)
    throw new Error(pickAxiosMessage(error, 'Error reassigning PMS job'))
  }
}

export const forceOverduePlanLineJob = async (
  planLineId: number,
  payload?: {
    jobDueReason?: string
    defectId?: number
    remarks?: string
    crewUndertaking?: string
  }
): Promise<PmsJobDto> => {
  try {
    const {data} = await axios.post(
      `${PMS_API_URL}/jobs/plan-lines/${planLineId}/force-overdue`,
      payload ?? {}
    )
    return data
  } catch (error) {
    console.error('Error forcing overdue job:', error)
    const message = pickAxiosMessage(error, 'Error creating unplanned job')
    const lowered = (message || '').toLowerCase()
    if (
      lowered.includes('duplicate entry') ||
      lowered.includes('could not execute statement') ||
      lowered.includes('constraint')
    ) {
      throw new Error('Unable to create unplanned job right now. Please try again.')
    }
    throw new Error(message)
  }
}

export const completePmsJob = async (id: number, dto: PmsJobCompletionDto): Promise<PmsJobDto> => {
  try {
    const {data} = await axios.post(`${PMS_API_URL}/jobs/${id}/complete`, dto)
    return data
  } catch (error) {
    console.error('Error completing PMS job:', error)
    throw new Error(pickAxiosMessage(error, 'Error completing PMS job'))
  }
}

export const completePlanLineJob = async (
  planLineId: number,
  dto: PmsJobCompletionDto
): Promise<PmsJobDto> => {
  try {
    const {data} = await axios.post(`${PMS_API_URL}/jobs/plan-lines/${planLineId}/complete`, dto)
    return data
  } catch (error) {
    console.error('Error completing plan line job:', error)
    throw new Error(pickAxiosMessage(error, 'Error completing plan line job'))
  }
}

export const verifyPmsJob = async (id: number, remarks?: string): Promise<PmsJobDto> => {
  try {
    const params = remarks ? {remarks} : {}
    const {data} = await axios.post(`${PMS_API_URL}/jobs/${id}/verify`, null, {params})
    return data
  } catch (error) {
    console.error('Error verifying PMS job:', error)
    throw new Error(pickAxiosMessage(error, 'Error verifying PMS job'))
  }
}

export const deferPmsJob = async (
  id: number,
  reason: string,
  newDueDate?: string
): Promise<PmsJobDto> => {
  try {
    const params: any = {reason}
    if (newDueDate) params.newDueDate = newDueDate
    const {data} = await axios.post(`${PMS_API_URL}/jobs/${id}/defer`, null, {params})
    return data
  } catch (error) {
    console.error('Error deferring PMS job:', error)
    throw new Error(pickAxiosMessage(error, 'Error deferring PMS job'))
  }
}

export const deferPlanLineJob = async (
  planLineId: number,
  reason: string,
  newDueDate?: string
): Promise<PmsJobDto> => {
  try {
    const params: any = {reason}
    if (newDueDate) params.newDueDate = newDueDate
    const {data} = await axios.post(
      `${PMS_API_URL}/jobs/plan-lines/${planLineId}/defer`,
      null,
      {params}
    )
    return data
  } catch (error) {
    console.error('Error deferring plan line job:', error)
    throw new Error(pickAxiosMessage(error, 'Error deferring plan line job'))
  }
}

export const cancelPmsJob = async (id: number, reason: string): Promise<PmsJobDto> => {
  try {
    const {data} = await axios.post(`${PMS_API_URL}/jobs/${id}/cancel`, null, {params: {reason}})
    return data
  } catch (error) {
    console.error('Error cancelling PMS job:', error)
    throw new Error(pickAxiosMessage(error, 'Error cancelling PMS job'))
  }
}

// Batch Operations
export const bulkDeferJobs = async (
  jobIds: number[],
  reason: string,
  newDueDate?: string
): Promise<BulkJobOperationResult> => {
  try {
    const {data} = await axios.post(`${PMS_API_URL}/jobs/batch/defer`, {
      jobIds,
      reason,
      newDueDate,
    })
    return data
  } catch (error) {
    console.error('Error deferring jobs in bulk:', error)
    throw new Error(pickAxiosMessage(error, 'Error deferring jobs in bulk'))
  }
}

export const bulkCancelJobs = async (
  jobIds: number[],
  reason: string
): Promise<BulkJobOperationResult> => {
  try {
    const {data} = await axios.post(`${PMS_API_URL}/jobs/batch/cancel`, {jobIds, reason})
    return data
  } catch (error) {
    console.error('Error cancelling jobs in bulk:', error)
    throw new Error(pickAxiosMessage(error, 'Error cancelling jobs in bulk'))
  }
}

export const getPmsJobHistory = async (jobId: number): Promise<PmsJobEventDto[]> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/jobs/${jobId}/history`)
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Error fetching job history:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching job history'))
  }
}

export const downloadPmsJobHistoryPdf = async (jobId: number): Promise<Blob> => {
  try {
    const response = await axios.get(`${PMS_API_URL}/jobs/${jobId}/history.pdf`, {
      responseType: 'blob',
    })
    return response.data
  } catch (error) {
    console.error('Error downloading job history PDF:', error)
    throw new Error(pickAxiosMessage(error, 'Error downloading job history PDF'))
  }
}

// ===============================
// JOB REQUESTS (from Defects)
// ===============================

export const getPendingJobRequests = async (vesselId?: number): Promise<QHSEDefectDto[]> => {
  try {
    const params: any = {}
    if (vesselId) params.vesselId = vesselId
    const {data} = await axios.get(`${PMS_API_URL}/jobs/requests`, {params})
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Error fetching pending job requests:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching pending job requests'))
  }
}

export const getJobRequestsPage = async (params: {
  vesselId?: number
  status?: Array<'PENDING' | 'ACCEPTED' | 'REJECTED' | 'NONE'>
  search?: string
  page?: number
  size?: number
  activeOnly?: boolean
  hasJobsOnly?: boolean
}): Promise<PagedResult<QHSEDefectDto>> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/jobs/requests/page`, {
      params: {
        vesselId: params.vesselId,
        status: params.status,
        search: params.search,
        page: params.page ?? 0,
        size: params.size ?? 10,
        activeOnly: params.activeOnly ?? true,
        hasJobsOnly: params.hasJobsOnly ?? true,
      },
      paramsSerializer: (p) => {
        const sp = new URLSearchParams()
        Object.entries(p).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') return
          if (Array.isArray(value)) {
            value.forEach((v) => {
              if (v != null && `${v}`.length > 0) sp.append(key, String(v))
            })
          } else {
            sp.append(key, String(value))
          }
        })
        return sp.toString()
      },
    })
    return {
      content: Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [],
      totalElements: data?.totalElements ?? (Array.isArray(data) ? data.length : 0),
      totalPages: data?.totalPages ?? 1,
      number: data?.number ?? 0,
      size: data?.size ?? params.size ?? 10,
    }
  } catch (error) {
    console.error('Error fetching job requests page:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching job requests'))
  }
}

export const acceptJobRequest = async (defectId: number, remarks?: string): Promise<PmsJobDto> => {
  try {
    const params: any = {}
    if (remarks) params.remarks = remarks
    const {data} = await axios.post(`${PMS_API_URL}/jobs/requests/${defectId}/accept`, null, {
      params,
    })
    return data
  } catch (error) {
    console.error('Error accepting job request:', error)
    throw new Error(pickAxiosMessage(error, 'Error accepting job request'))
  }
}

export const rejectJobRequest = async (defectId: number, reason: string): Promise<void> => {
  try {
    await axios.post(`${PMS_API_URL}/jobs/requests/${defectId}/reject`, null, {params: {reason}})
  } catch (error) {
    console.error('Error rejecting job request:', error)
    throw new Error(pickAxiosMessage(error, 'Error rejecting job request'))
  }
}

// Postponement Requests
export const createPostponementRequest = async (
  dto: Partial<PmsPostponementRequestDto>
): Promise<PmsPostponementRequestDto> => {
  try {
    const {data} = await axios.post(`${PMS_API_URL}/postponements`, dto)
    return data
  } catch (error) {
    console.error('Error creating postponement request:', error)
    throw new Error(pickAxiosMessage(error, 'Error creating postponement request'))
  }
}

export const approvePostponementRequest = async (
  id: number,
  remarks?: string
): Promise<PmsPostponementRequestDto> => {
  try {
    const {data} = await axios.post(`${PMS_API_URL}/postponements/${id}/approve`, null, {
      params: remarks ? {remarks} : {},
    })
    return data
  } catch (error) {
    console.error('Error approving postponement request:', error)
    throw new Error(pickAxiosMessage(error, 'Error approving postponement request'))
  }
}

export const rejectPostponementRequest = async (
  id: number,
  rejectionReason: string
): Promise<PmsPostponementRequestDto> => {
  try {
    const {data} = await axios.post(`${PMS_API_URL}/postponements/${id}/reject`, null, {
      params: {rejectionReason},
    })
    return data
  } catch (error) {
    console.error('Error rejecting postponement request:', error)
    throw new Error(pickAxiosMessage(error, 'Error rejecting postponement request'))
  }
}

export const getPendingPostponementRequests = async (): Promise<PmsPostponementRequestDto[]> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/postponements/pending`)
    return data
  } catch (error) {
    console.error('Error fetching pending postponement requests:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching pending postponement requests'))
  }
}

export const getPostponementRequestsByJob = async (
  jobId: number
): Promise<PmsPostponementRequestDto[]> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/postponements/job/${jobId}`)
    return data
  } catch (error) {
    console.error('Error fetching postponement requests:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching postponement requests'))
  }
}

export const generateDueJobs = async (): Promise<{generated: number; message: string}> => {
  try {
    const {data} = await axios.post(`${PMS_API_URL}/jobs/generate-due-jobs`)
    return data
  } catch (error) {
    console.error('Error generating due jobs:', error)
    throw new Error(pickAxiosMessage(error, 'Error generating due jobs'))
  }
}

// ===============================
// JOB ATTACHMENTS
// ===============================

export const listJobAttachments = async (jobId: number): Promise<PmsJobAttachmentDto[]> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/jobs/${jobId}/attachments`)
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Error listing job attachments:', error)
    throw new Error(pickAxiosMessage(error, 'Error listing job attachments'))
  }
}

export const listWorkOrdersByPlanLine = async (planLineId: number): Promise<PmsJobDto[]> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/jobs/plan-lines/${planLineId}/work-orders`)
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Error loading work orders:', error)
    throw new Error(pickAxiosMessage(error, 'Error loading work orders'))
  }
}

export const recordPlanLineLastDone = async (
  planLineId: number,
  payload: {lastDoneDate?: string | null; lastDoneCounter?: number | null}
): Promise<PmsJobDto> => {
  try {
    const {data} = await axios.post(
      `${PMS_API_URL}/jobs/plan-lines/${planLineId}/last-done`,
      payload
    )
    return data
  } catch (error) {
    console.error('Error recording last done:', error)
    throw new Error(pickAxiosMessage(error, 'Failed to record last done'))
  }
}

export const getJobAttachment = async (attachmentId: number): Promise<PmsJobAttachmentDto> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/jobs/attachments/${attachmentId}`)
    return data
  } catch (error) {
    console.error('Error fetching job attachment:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching job attachment'))
  }
}

export const uploadJobAttachments = async (
  jobId: number,
  files: File[],
  fileType: PmsJobAttachmentFileType,
  uploadedBy?: string
): Promise<PmsJobAttachmentDto[]> => {
  try {
    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))
    formData.append('fileType', fileType)
    if (uploadedBy) formData.append('uploadedBy', uploadedBy)
    const {data} = await axios.post(`${PMS_API_URL}/jobs/${jobId}/attachments`, formData, {
      headers: {'Content-Type': 'multipart/form-data'},
    })
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Error uploading job attachments:', error)
    throw new Error(pickAxiosMessage(error, 'Error uploading job attachments'))
  }
}

export const deleteJobAttachment = async (jobId: number, attachmentId: number): Promise<void> => {
  try {
    await axios.delete(`${PMS_API_URL}/jobs/${jobId}/attachments/${attachmentId}`)
  } catch (error) {
    console.error('Error deleting job attachment:', error)
    throw new Error(pickAxiosMessage(error, 'Error deleting job attachment'))
  }
}

const API_ORIGIN = (API_URL || '').replace(/\/api\/?$/, '')

const startsWithUploads = (p: string) => /^\/?uploads\//i.test(p)
const looksLikeFsPath = (p: string) =>
  /^([A-Za-z]:\\|\/(home|var|opt|srv|tmp|etc|Users|mnt)\/)/i.test(p)
const isHttp = (p: string) => /^https?:\/\//i.test(p)

const buildJobAttachmentUrl = (
  attachment: PmsJobAttachmentDto,
  kind: 'view' | 'download'
): string => {
  const p = attachment.filePath || ''
  if (p && !looksLikeFsPath(p)) {
    if (isHttp(p)) return p
    if (startsWithUploads(p)) {
      return `${API_ORIGIN}${p.startsWith('/') ? '' : '/'}${p}`
    }
    return `${API_ORIGIN}/${p.replace(/^\/+/, '')}`
  }

  if (attachment.id) {
    return `${PMS_API_URL}/jobs/attachments/${attachment.id}/${kind}`
  }

  return `${API_ORIGIN}${p.startsWith('/') ? '' : '/'}${p}`
}

export const jobAttachmentViewUrl = (attachment: PmsJobAttachmentDto): string =>
  buildJobAttachmentUrl(attachment, 'view')

export const jobAttachmentDownloadUrl = (attachment: PmsJobAttachmentDto): string =>
  buildJobAttachmentUrl(attachment, 'download')

// ===============================
// JOB SPARE USAGES
// ===============================

export const listJobSpareUsages = async (jobId: number): Promise<PmsJobSpareUsageDto[]> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/jobs/${jobId}/spare-usages`)
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Error listing job spare usages:', error)
    throw new Error(pickAxiosMessage(error, 'Error listing job spare usages'))
  }
}

// ===============================
// REPORTS
// ===============================

export const getComplianceReport = async (
  vesselId?: number,
  fromDate?: string,
  toDate?: string
): Promise<ComplianceReportDto> => {
  try {
    const params: any = {}
    if (vesselId) params.vesselId = vesselId
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    const {data} = await axios.get(`${PMS_API_URL}/reports/compliance`, {params})
    return data
  } catch (error) {
    console.error('Error fetching compliance report:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching compliance report'))
  }
}

export const getJobHistoryReport = async (
  vesselId?: number,
  fromDate?: string,
  toDate?: string,
  state?: PmsJobState
): Promise<JobHistoryReportDto[]> => {
  try {
    const params: any = {}
    if (vesselId) params.vesselId = vesselId
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    if (state) params.state = state
    const {data} = await axios.get(`${PMS_API_URL}/reports/job-history`, {params})
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Error fetching job history report:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching job history report'))
  }
}

export const getOverhaulHistoryReport = async (
  vesselId?: number,
  fromDate?: string,
  toDate?: string
): Promise<OverhaulHistoryReportDto[]> => {
  try {
    const params: any = {}
    if (vesselId) params.vesselId = vesselId
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    const {data} = await axios.get(`${PMS_API_URL}/reports/overhaul-history`, {params})
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Error fetching overhaul history report:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching overhaul history report'))
  }
}

export const getSparesConsumptionReport = async (
  vesselId?: number,
  equipmentId?: number,
  componentId?: number,
  fromDate?: string,
  toDate?: string
): Promise<SparesConsumptionReportDto[]> => {
  try {
    const params: any = {}
    if (vesselId) params.vesselId = vesselId
    if (equipmentId) params.equipmentId = equipmentId
    if (componentId) params.componentId = componentId
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    const {data} = await axios.get(`${PMS_API_URL}/reports/spares-consumption`, {params})
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Error fetching spares consumption report:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching spares consumption report'))
  }
}

export const getDefectsIntegrationReport = async (
  vesselId?: number,
  fromDate?: string,
  toDate?: string
): Promise<DefectsIntegrationReportDto> => {
  try {
    const params: any = {}
    if (vesselId) params.vesselId = vesselId
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    const {data} = await axios.get(`${PMS_API_URL}/reports/defects-integration`, {params})
    return data
  } catch (error) {
    console.error('Error fetching defects integration report:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching defects integration report'))
  }
}

export const getSparesConsumptionReportPage = async (
  vesselId?: number,
  equipmentId?: number,
  componentId?: number,
  categoryId?: number,
  fromDate?: string,
  toDate?: string,
  page: number = 0,
  size: number = 10
): Promise<PagedResult<SparesConsumptionReportDto>> => {
  try {
    const params: any = {page, size}
    if (vesselId) params.vesselId = vesselId
    if (equipmentId) params.equipmentId = equipmentId
    if (componentId) params.componentId = componentId
    if (categoryId) params.categoryId = categoryId
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    const {data} = await axios.get(`${PMS_API_URL}/reports/spares-consumption/page`, {params})
    return data || {content: [], totalPages: 0, totalElements: 0, number: page, size}
  } catch (error) {
    console.error('Error fetching spares consumption report page:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching spares consumption report page'))
  }
}

export const getJobHistoryReportPage = async (
  vesselId?: number,
  fromDate?: string,
  toDate?: string,
  state?: PmsJobState,
  page: number = 0,
  size: number = 10
): Promise<PagedResult<JobHistoryReportDto>> => {
  try {
    const params: any = {page, size}
    if (vesselId) params.vesselId = vesselId
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    if (state) params.state = state
    const {data} = await axios.get(`${PMS_API_URL}/reports/job-history/page`, {params})
    return data || {content: [], totalPages: 0, totalElements: 0, number: page, size}
  } catch (error) {
    console.error('Error fetching job history report page:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching job history report page'))
  }
}

export const getDefectJobsReport = async (
  vesselId?: number,
  fromDate?: string,
  toDate?: string,
  state?: PmsJobState
): Promise<DefectJobReportDto[]> => {
  try {
    const params: any = {}
    if (vesselId) params.vesselId = vesselId
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    if (state) params.state = state
    const {data} = await axios.get(`${PMS_API_URL}/reports/defects-jobs`, {params})
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Error fetching defect jobs report:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching defect jobs report'))
  }
}

export const getDefectJobsReportPage = async (
  vesselId?: number,
  fromDate?: string,
  toDate?: string,
  state?: PmsJobState,
  page: number = 0,
  size: number = 10
): Promise<PagedResult<DefectJobReportDto>> => {
  try {
    const params: any = {page, size}
    if (vesselId) params.vesselId = vesselId
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    if (state) params.state = state
    const {data} = await axios.get(`${PMS_API_URL}/reports/defects-jobs/page`, {params})
    return data || {content: [], totalPages: 0, totalElements: 0, number: page, size}
  } catch (error) {
    console.error('Error fetching defect jobs report page:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching defect jobs report page'))
  }
}

// ===============================
// REPORT EXPORTS
// ===============================

export const exportComplianceReport = async (
  vesselId?: number,
  fromDate?: string,
  toDate?: string
): Promise<void> => {
  try {
    const params: any = {}
    if (vesselId) params.vesselId = vesselId
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    const response = await axios.get(`${PMS_API_URL}/reports/compliance/export`, {
      params,
      responseType: 'blob',
    })
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `compliance-report-${new Date().toISOString().split('T')[0]}.csv`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error exporting compliance report:', error)
    throw new Error(pickAxiosMessage(error, 'Error exporting compliance report'))
  }
}

export const exportJobHistoryReport = async (
  vesselId?: number,
  fromDate?: string,
  toDate?: string,
  state?: PmsJobState
): Promise<void> => {
  try {
    const params: any = {}
    if (vesselId) params.vesselId = vesselId
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    if (state) params.state = state
    const response = await axios.get(`${PMS_API_URL}/reports/job-history/export`, {
      params,
      responseType: 'blob',
    })
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute(
      'download',
      `job-history-report-${new Date().toISOString().split('T')[0]}.csv`
    )
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error exporting job history report:', error)
    throw new Error(pickAxiosMessage(error, 'Error exporting job history report'))
  }
}

export const exportOverhaulHistoryReport = async (
  vesselId?: number,
  fromDate?: string,
  toDate?: string
): Promise<void> => {
  try {
    const params: any = {}
    if (vesselId) params.vesselId = vesselId
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    const response = await axios.get(`${PMS_API_URL}/reports/overhaul-history/export`, {
      params,
      responseType: 'blob',
    })
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute(
      'download',
      `overhaul-history-report-${new Date().toISOString().split('T')[0]}.csv`
    )
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error exporting overhaul history report:', error)
    throw new Error(pickAxiosMessage(error, 'Error exporting overhaul history report'))
  }
}

export const exportSparesConsumptionReport = async (
  vesselId?: number,
  equipmentId?: number,
  componentId?: number,
  fromDate?: string,
  toDate?: string
): Promise<void> => {
  try {
    const params: any = {}
    if (vesselId) params.vesselId = vesselId
    if (equipmentId) params.equipmentId = equipmentId
    if (componentId) params.componentId = componentId
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    const response = await axios.get(`${PMS_API_URL}/reports/spares-consumption/export`, {
      params,
      responseType: 'blob',
    })
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute(
      'download',
      `spares-consumption-report-${new Date().toISOString().split('T')[0]}.csv`
    )
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error exporting spares consumption report:', error)
    throw new Error(pickAxiosMessage(error, 'Error exporting spares consumption report'))
  }
}

export const exportDefectsIntegrationReport = async (
  vesselId?: number,
  fromDate?: string,
  toDate?: string
): Promise<void> => {
  try {
    const params: any = {}
    if (vesselId) params.vesselId = vesselId
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    const response = await axios.get(`${PMS_API_URL}/reports/defects-integration/export`, {
      params,
      responseType: 'blob',
    })
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute(
      'download',
      `defects-integration-report-${new Date().toISOString().split('T')[0]}.csv`
    )
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error exporting defects integration report:', error)
    throw new Error(pickAxiosMessage(error, 'Error exporting defects integration report'))
  }
}

export const exportDefectJobsReport = async (
  vesselId?: number,
  fromDate?: string,
  toDate?: string,
  state?: PmsJobState
): Promise<void> => {
  try {
    const params: any = {}
    if (vesselId) params.vesselId = vesselId
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    if (state) params.state = state
    const response = await axios.get(`${PMS_API_URL}/reports/defects-jobs/export`, {
      params,
      responseType: 'blob',
    })
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute(
      'download',
      `defect-jobs-report-${new Date().toISOString().split('T')[0]}.csv`
    )
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error exporting defect jobs report:', error)
    throw new Error(pickAxiosMessage(error, 'Error exporting defect jobs report'))
  }
}

// ===============================
// RUNNING HOURS
// ===============================

export const getRunningHourCounters = async (
  vesselId?: number
): Promise<RunningHourCounterDto[]> => {
  try {
    const params = vesselId ? {vesselId} : {}
    const {data} = await axios.get(`${PMS_API_URL}/running-hours/counters`, {params})
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Error fetching running hour counters:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching running hour counters'))
  }
}

export const getRunningHourCounter = async (id: number): Promise<RunningHourCounterDto> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/running-hours/counters/${id}`)
    return data
  } catch (error) {
    console.error('Error fetching running hour counter:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching running hour counter'))
  }
}

export const createRunningHourCounter = async (
  dto: RunningHourCounterDto
): Promise<RunningHourCounterDto> => {
  try {
    const {data} = await axios.post(`${PMS_API_URL}/running-hours/counters`, dto)
    return data
  } catch (error) {
    console.error('Error creating running hour counter:', error)
    throw new Error(pickAxiosMessage(error, 'Error creating running hour counter'))
  }
}

export const updateRunningHourCounter = async (
  id: number,
  dto: Partial<RunningHourCounterDto>
): Promise<RunningHourCounterDto> => {
  try {
    const {data} = await axios.put(`${PMS_API_URL}/running-hours/counters/${id}`, dto)
    return data
  } catch (error) {
    console.error('Error updating running hour counter:', error)
    throw new Error(pickAxiosMessage(error, 'Error updating running hour counter'))
  }
}

/** Superadmin (role 1) only — backend enforces. Removes all readings/history and the counter; detaches from PMS plan lines. */
export const deleteRunningHourCounterSuperAdmin = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${PMS_API_URL}/running-hours/counters/${id}`)
  } catch (error) {
    console.error('Error deleting running hour counter:', error)
    throw new Error(pickAxiosMessage(error, 'Error deleting running hour counter'))
  }
}

export const deleteRunningHourCounter = deleteRunningHourCounterSuperAdmin

export const getRunningHourReadings = async (
  counterId: number
): Promise<RunningHourReadingDto[]> => {
  try {
    const {data} = await axios.get(`${PMS_API_URL}/running-hours/counters/${counterId}/readings`)
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Error fetching running hour readings:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching running hour readings'))
  }
}

export const createRunningHourReading = async (
  counterId: number,
  dto: RunningHourReadingDto
): Promise<RunningHourReadingDto> => {
  try {
    const {data} = await axios.post(
      `${PMS_API_URL}/running-hours/counters/${counterId}/readings`,
      dto
    )
    return data
  } catch (error) {
    console.error('Error creating running hour reading:', error)
    throw new Error(pickAxiosMessage(error, 'Error creating running hour reading'))
  }
}

export const getRunningHourReadingsPaged = async (
  counterId: number,
  page: number,
  size: number
): Promise<PagedResult<RunningHourReadingDto>> => {
  try {
    const {data} = await axios.get(
      `${PMS_API_URL}/running-hours/counters/${counterId}/readings/history`,
      {
        params: {page, size},
      }
    )
    return data
  } catch (error) {
    console.error('Error fetching running hour reading history:', error)
    throw new Error(pickAxiosMessage(error, 'Error fetching running hour reading history'))
  }
}

export const estimateRunningHourReadingImpact = async (
  counterId: number,
  dto: RunningHourReadingDto
): Promise<RunningHourReadingImpactDto> => {
  try {
    const {data} = await axios.post(
      `${PMS_API_URL}/running-hours/counters/${counterId}/readings/impact`,
      dto
    )
    return data
  } catch (error) {
    console.error('Error estimating running hour impact:', error)
    throw new Error(pickAxiosMessage(error, 'Error estimating running hour impact'))
  }
}

export const updateRunningHourReading = async (
  readingId: number,
  dto: RunningHourReadingDto
): Promise<RunningHourReadingDto> => {
  try {
    const {data} = await axios.put(`${PMS_API_URL}/running-hours/readings/${readingId}`, dto)
    return data
  } catch (error) {
    console.error('Error updating running hour reading:', error)
    throw new Error(pickAxiosMessage(error, 'Error updating running hour reading'))
  }
}
export const bulkUploadRunningHourReadings = async (file: File, vesselId: number): Promise<any> => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('vesselId', vesselId.toString())
    const {data} = await axios.post(`${PMS_API_URL}/running-hours/readings/bulk-upload`, formData, {
      headers: {'Content-Type': 'multipart/form-data'},
    })
    return data
  } catch (error) {
    console.error('Error uploading running hour readings bulk file:', error)
    throw new Error(pickAxiosMessage(error, 'Error uploading running hour readings'))
  }
}
